import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { AgsReportCountry } from '~/exportReport/exportReport.constants';
import { getReportSampleCenter } from '~/exportReport/exportReport.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import {
  EXPORT_REPORT_AVAILABLE_AREAS, type ExportReportTemplateSelectorArea,
} from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import type { ExportReportAreaQuery } from '~/exportReport/validation/exportReportAreas.worker.helpers';
import { getExportReportAreas } from '~/exportReport/validation/getExportReportAreas';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useModal } from '~/modal/useModal.hook';
import {
  exportReportEnable, exportReportSetSettings,
} from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import type { ExportReportActiveParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

export const useExportReport = () => {
  const { replaceCurrentModal: openExportReportTemplateSelectorModal } = useModal('ExportReportTemplateSelector');
  const { replaceCurrentModal: openExportReportPreviewModal } = useModal('ExportReportPreview');
  const exportReportState = useToolsStateExportReportSelector();
  const latLngLookup = useLatLngSpreadsheetData();
  const dispatch = useDispatch();

  const openExportReport = useCallback(async (params: ExportReportActiveParams) => {
    if (!CONFIG.ENABLE_PDF_EXPORT) {
      return;
    }

    dispatch(exportReportEnable(params));

    let query: ExportReportAreaQuery | undefined;

    switch (params.type) {
      case ExportReportType.Location: {
        const locationMarkerCenter = latLngLookup.getRow(params.data.markerId);
        query = locationMarkerCenter ? { type: 'point', point: locationMarkerCenter } : undefined;
        break;
      }

      case ExportReportType.Radius:
        query = { type: 'point', point: params.data.center };
        break;

      case ExportReportType.DriveTimePolygon:
      case ExportReportType.Polygon:
        query = { type: 'polygon', paths: params.data.paths };
        break;

      default:
        break;
    }

    let reportAreas: ReadonlyArray<ExportReportTemplateSelectorArea> = [];

    if (query) {
      // A failed lookup falls back to offering every area rather than leaving the enabled report without a modal.
      reportAreas = await getExportReportAreas(query).catch(() => []);
    }

    openExportReportTemplateSelectorModal({
      reportAreas: reportAreas.length ? reportAreas : EXPORT_REPORT_AVAILABLE_AREAS,
    });
  }, [dispatch, latLngLookup, openExportReportTemplateSelectorModal]);

  const setReportSettings = useCallback((template: ReportTemplate, country: AgsReportCountry) => {
    dispatch(exportReportSetSettings({
      template,
      country,
      isSampleReport: false,
    }));
    openExportReportPreviewModal({});
  }, [dispatch, openExportReportPreviewModal]);

  const setSampleReportSettings = useCallback((template: ReportTemplate, country: AgsReportCountry) => {
    const sampleReportsCenter = getReportSampleCenter(country);
    const originalParams = exportReportState.params;

    dispatch(exportReportEnable({
      type: ExportReportType.Radius,
      data: {
        center: sampleReportsCenter,
        radius: 5,
        title: '',
        unit: UnitSystem.imperial,
      },
    }));
    dispatch(exportReportSetSettings({
      template,
      country,
      isSampleReport: true,
    }));
    openExportReportPreviewModal({
      onBackToReportsClick: () => {
        if (originalParams) {
          openExportReport(originalParams);
        }
      },
    });
  }, [dispatch, exportReportState.params, openExportReport, openExportReportPreviewModal]);

  return {
    openExportReport,
    setReportSettings,
    setSampleReportSettings,
  };
};
